import { Component, OnInit, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AppState } from 'src/app/store';
import * as BusinessActions from 'src/app/store/business/business.actions';
import * as BusinessSelectors from 'src/app/store/business/business.selectors';
import { BusinessInformationEntity } from 'src/app/models/business.model';
import { FeedbackService } from 'src/app/services/feedback/feedback.service';
import { BusinessService } from 'src/app/services/business/business.service';
import { FeedbackStatsEntity } from 'src/app/models/feedback.model';
import { Observable, Subscription, BehaviorSubject, combineLatest, forkJoin, of } from 'rxjs';
import { take, map, catchError, switchMap } from 'rxjs/operators';

interface BusinessWithStats extends BusinessInformationEntity {
  storeStats?: FeedbackStatsEntity;
}

@Component({
  selector: 'app-explore-collection',
  templateUrl: './explore-collection.component.html',
  styleUrls: ['./explore-collection.component.css'],
})
export class ExploreCollectionComponent implements OnInit, OnDestroy {
  businesses$: Observable<BusinessInformationEntity[]> = this.store.select(
    BusinessSelectors.selectFilteredBusinesses
  );
  isLoading$: Observable<boolean> = this.store.select(
    BusinessSelectors.selectBusinessLoading
  );
  error$: Observable<string | null> = this.store.select(
    BusinessSelectors.selectBusinessError
  );
  currentZipCode$: Observable<string | null> = this.store.select(
    BusinessSelectors.selectCurrentZipCode
  );

  topRatedStores$ = new BehaviorSubject<BusinessWithStats[]>([]);
  topRatedStoreImageLoadingStates: Map<string, boolean> = new Map();
  businessImageLoadingStates: Map<string, boolean> = new Map();

  private subscriptions = new Subscription();
  private hasRequestedLocation = false;
  private currentLatitude: number | null = null;
  private currentLongitude: number | null = null;
  private readonly itemsPerPage = 6; // Show 18 businesses initially

  constructor(
    private store: Store<AppState>, 
    private router: Router,
    private feedbackService: FeedbackService,
    private businessService: BusinessService
  ) {}

  businessesWithStats$: Observable<(BusinessInformationEntity & { storeStats?: FeedbackStatsEntity })[]> = combineLatest([
    this.businesses$,
    this.feedbackService.getAllFeedbackStats().pipe(
      catchError(() => of([]))
    )
  ]).pipe(
    map(([businesses, stats]) => {
      const statsMap = new Map<string, FeedbackStatsEntity>();
      stats.forEach(stat => statsMap.set(stat.storeId, stat));
      
      const businessesWithStats = businesses.map(business => {
        const businessWithStats = business as BusinessInformationEntity & { storeStats?: FeedbackStatsEntity };
        return {
          ...businessWithStats,
          storeStats: statsMap.get(business.id) || businessWithStats.storeStats
        };
      });

      // Initialize loading states for business images
      businessesWithStats.forEach((business) => {
        this.businessImageLoadingStates.set(business.id, true);
      });

      return businessesWithStats;
    })
  );

  ngOnInit(): void {
    this.loadTopRatedStores();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadTopRatedStores(): void {
    // Get all feedback stats
    this.subscriptions.add(
      this.feedbackService.getAllFeedbackStats().pipe(
        switchMap((stats) => {
          if (stats.length === 0) {
            return of([]);
          }

          // Sort by totalHeartsLiked descending and take top 2
          const topStats = stats
            .sort((a, b) => (b.totalHeartsLiked || 0) - (a.totalHeartsLiked || 0))
            .slice(0, 2);

          // Fetch business details for top 2 stores
          const businessRequests = topStats.map(stat =>
            this.businessService.findById(stat.storeId).pipe(
              map((business) => ({
                ...business,
                storeStats: stat,
              } as BusinessWithStats)),
              catchError(() => of(null))
            )
          );

          return forkJoin(businessRequests).pipe(
            map((businesses) => businesses.filter(b => b !== null) as BusinessWithStats[])
          );
        }),
        catchError((error) => {
          console.error('Error loading top rated stores:', error);
          return of([]);
        })
      ).subscribe((stores) => {
        // Initialize loading states for top rated store images
        stores.forEach((store) => {
          this.topRatedStoreImageLoadingStates.set(store.id, true);
        });
        this.topRatedStores$.next(stores);
      })
    );
  }

  goToBusinessDetail(businessId: string): void {
    this.router.navigate(['/businesses', businessId]);
  }

  formatLikes(likes: number): string {
    if (likes >= 1000) {
      return (likes / 1000).toFixed(likes % 1000 !== 0 ? 1 : 0) + 'K';
    }
    return likes.toString();
  }

  getStoreImage(store: BusinessWithStats): string {
    return store.banner || store.storeLogo || 'assets/img/restaurants/540x300/collection-1.jpg';
  }

  onTopRatedStoreImageLoad(storeId: string): void {
    this.topRatedStoreImageLoadingStates.set(storeId, false);
  }

  onTopRatedStoreImageError(storeId: string): void {
    this.topRatedStoreImageLoadingStates.set(storeId, false);
  }

  isTopRatedStoreImageLoading(storeId: string): boolean {
    return this.topRatedStoreImageLoadingStates.get(storeId) !== false;
  }

  onBusinessImageLoad(businessId: string): void {
    this.businessImageLoadingStates.set(businessId, false);
  }

  onBusinessImageError(businessId: string): void {
    this.businessImageLoadingStates.set(businessId, false);
  }

  isBusinessImageLoading(businessId: string): boolean {
    return this.businessImageLoadingStates.get(businessId) !== false;
  }
}
